import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { Avatar, Card, PaperIcon, Select, Spin } from '@nackle/paper';
import { LocaleString, getString } from '@nackle/intl-tools';
import classNames from 'classnames';
import { submitRecognition } from '../../../app-dux/recognition/actions';
import { searchProgramsAndRewardChoices, setSelectedBehavior, setSelectedProgram } from './actions';
import { setPointsForPerson, setAllocatePoints } from '../awards/actions';
import { setPrivate } from '../message/actions';
import { getBehaviors, getSelectedBehaviorId } from '../program/selectors';
import { getErrors } from '../../../app-dux/app-dux';
import { getRewardChoices, getSelectedProgram } from './selectors';
import { Circle, CircleCheck } from '../../shared/icons/icons.js';
import Awards from '../awards/Awards';

const { Option } = Select;

const iconFill = {
    'CHROME': {
        fill: '#1a73e8',
    },
    'TEAMS': {
        fill: '#6266a5',
    },
    'WORKPLACE': {
        fill: '#1a73e8',
    },
};

export default function Program( props ) {
    const { company,
        forward,
        ineligibleRecipients,
        integrationType } = props;
    const dispatch = useDispatch();
    const rewardChoices = useSelector( ( state ) => getRewardChoices( state ) );
    const selectedProgram = useSelector( ( state ) => getSelectedProgram( state ) );
    const behaviors = useSelector( ( state ) => getBehaviors( state ) );
    const selectedBehaviorId = useSelector( ( state ) => getSelectedBehaviorId( state ) );
    const errors = useSelector( ( state ) => getErrors( state ) );
    const [ animate, setAnimate ] = useState( false );
    const [ programs, setPrograms ] = useState( [] );
    const [ programValidating, setProgramValidating ] = useState( false );
    const [ budgetError, setBudgetError ] = useState( '' );

    useEffect( () => {
        setAnimate( true );
        dispatch( searchProgramsAndRewardChoices() );
    }, [] );

    useEffect( () => {
        //Construct the list of programs
        const programList = [];
        rewardChoices.map( choice => {
            const program = { ...choice.program };
            if ( choice.rewardStack && choice.rewardStack.rewards && choice.rewardStack.rewards.length > 0 ) {
                const hasPoints = choice.rewardStack.rewards.filter( reward => { return reward.type === 'POINTS'; } );
                program.hasPoints = hasPoints.length > 0;
                if ( hasPoints.length > 0 ) {

                    program.minAward = hasPoints[ 0 ].reward.minValue;
                    program.maxAward = hasPoints[ 0 ].reward.maxValue;
                    program.hasPoints = true;
                    program.pointsRequired = choice.requiredRewardTypes.includes( 'POINTS' );
                    program.isFixed = hasPoints[ 0 ].reward.inputType === 'FIXED';
                }
                const ecards = choice.rewardStack.rewards.filter( reward => { return reward.type === 'ECARD'; } );
                program.ecards = ecards;
                program.ecardRequired = choice.requiredRewardTypes.includes( 'ECARD' );
            }
            program.rewardChoiceId = choice.id;
            program.rewardChoiceName = choice.name;
            if ( choice.activity && choice.activity.activityConfig && choice.activity.activityConfig.visibility ) {
                program.visibility = choice.activity.activityConfig.visibility;
            }
            if ( choice.activity && choice.activity.activityConfig && choice.activity.activityConfig.behaviors ) {
                program.behaviors = choice.activity.activityConfig.behaviors;
                program.isBehaviorRequired = choice.activity.activityConfig.isBehaviorRequired;
            }
            if ( choice.budgets && choice.budgets.length ) {
                choice.budgets.map( budget => {
                    const { endDate } = budget;
                    if ( endDate ) {
                        const numOfDays = ( new Date( endDate ).getTime() - new Date().getTime() ) / ( 1000 * 60 * 60 * 24 );
                        const niceNumOfDays = Math.round( numOfDays );
                        return budget.endDays = niceNumOfDays;
                    }
                } );
                program.budgets = choice.budgets;

                const programBudgets = choice.budgets.filter( budget => { return budget.foreignIds && budget.foreignIds.length > 0 && budget.foreignIds.filter( foreign => { return foreign.foreignIdType === 'PROGRAM_ID'; } ); } );

                if (
                    programBudgets &&
                    programBudgets.length === 1 &&
                    programBudgets[ 0 ].foreignIds.filter( foreign => { return foreign.foreignIdType === 'PROGRAM_ID' && foreign.foreignId === program.id; } )
                ) {
                    program.pointsDisplay = programBudgets[ 0 ].convertedCurrentValue;
                    program.endDays = programBudgets[ 0 ].endDays;
                } else if ( programBudgets && programBudgets.length > 1 ) {

                    const maxBudget = programBudgets.sort( ( a, b ) => b.convertedCurrentValue - a.convertedCurrentValue )[ 0 ];
                    program.pointsDisplay = maxBudget.convertedCurrentValue;
                    program.endDays = maxBudget.endDays;
                } else {
                    const maxBudget = choice.budgets.sort( ( a, b ) => b.convertedCurrentValue - a.convertedCurrentValue )[ 0 ];
                    program.pointsDisplay = maxBudget.convertedCurrentValue;
                    program.endDays = maxBudget.endDays;

                }
            }
            programList.push( program );
        } );
        setPrograms( programList );
    }, [ rewardChoices ] );

    useEffect( () => {
        const validateRecognition = async () => {
            const results = await dispatch( submitRecognition( integrationType, 'VALIDATED' ) );

            setProgramValidating( false );
            if ( !results.errors || results.errors.length === 0 ) {
                if ( selectedProgram.isFixed && selectedProgram.pointsRequired ) {
                    dispatch( setPointsForPerson( 'all', selectedProgram.maxAward ) );
                }
            }
            if ( selectedProgram.pointsRequired ) {
                if ( ( !selectedProgram.budgets || selectedProgram.budgets.length === 0 ) ||
                    ( selectedProgram.budgets[ 0 ].convertedPointValue === 0 ) ) {
                    setBudgetError( getString( 'recognition.error.noBudget' ) );
                }
            }

        };
        if ( selectedProgram.id ) {
            validateRecognition();
        }
    }, [ selectedProgram ] );

    const iconFillColor = iconFill[ integrationType.toUpperCase() ].fill;

    const programSelect = async ( program ) => {
        setProgramValidating( true );
        setBudgetError( '' );
        dispatch( setSelectedBehavior( '' ) );
        dispatch( setSelectedProgram( program ) );
        dispatch( setPointsForPerson( 'all', 0 ) );
        dispatch( setAllocatePoints( false ) );
        dispatch( setPrivate( program.visibility && program.visibility.length === 1 && program.visibility[ 0 ] === 'PRIVATE' ) );
    };
    return (
        <CSSTransition
            timeout={ 1000 }
            in={ animate }
            classNames={ forward ? 'slide-in' : 'slide-out' }>
            <div>
                <span className='input-label'><LocaleString code='recognition.program.header' /></span>
                <span className='input-label-extra'><LocaleString code='recognition.header.required' /></span>
                <div className='program-list'>
                    { programs.map( program => {
                        return <div data-testid={ `cardDiv-${ program.rewardChoiceId }` } key={ `program-${ program.rewardChoiceId }` } onClick={ () => programSelect( program ) }>
                            <Card className={ selectedProgram.rewardChoiceId === program.rewardChoiceId ? 'program-item program-item-selected' : 'program-item' }>
                                <div className='program-top-card'>
                                    <div className='program-info'>
                                        <div className='program-name'>
                                            { program.rewardChoiceName }
                                        </div>
                                        { program.numSent && program.lastSent &&
                                            <div className='program-detail'>
                                                { `${ program.numSent } sent - last sent: ${ program.lastSent }` }
                                            </div>
                                        }
                                    </div>
                                    <div className='program-select'>
                                        {
                                            selectedProgram.rewardChoiceId === program.rewardChoiceId
                                                ?
                                                programValidating
                                                    ?
                                                    <Spin size='small' />
                                                    :
                                                    <PaperIcon icon={ <CircleCheck fill={ iconFillColor } /> } />
                                                :
                                                <PaperIcon icon={ <Circle className="circle-icon" fill="#bcbcbc" /> } />
                                        }
                                    </div>
                                </div>
                                <div className='program-bottom-card'>
                                    {
                                        program.hasPoints && program.budgets && program.budgets.length > 0 &&
                                        <span>
                                            <span><LocaleString code="recognition.program.availablePoints" /></span>
                                            <span className={ classNames( { 'points-available': true, 'balance-empty': program.budgets[ 0 ].convertedPointValue === 0 } ) }>{ program.budgets[ 0 ].convertedPointValue } <LocaleString code="mediaLabel" /></span>
                                            { program.budgets[ 0 ].endDays &&
                                                <span>· <LocaleString code="recognition.program.expiresText" replaceOptions={ { expireDays: program.budgets[ 0 ].endDays } } /></span>
                                            }
                                        </span>
                                    }

                                    { selectedProgram.rewardChoiceId === program.rewardChoiceId
                                        ?
                                        [
                                            ineligibleRecipients && ineligibleRecipients.length > 0 &&
                                            <div className='error-item'>
                                                { `${ ineligibleRecipients.length } ${ ineligibleRecipients.length > 1
                                                    ? getString( 'recognition.program.ineligibleMulti' )
                                                    : getString( 'recognition.program.ineligibleSingle' ) }` }
                                            </div>,

                                            budgetError &&
                                            <div className='error-item'>
                                                { budgetError }
                                            </div>
                                        ]
                                        :
                                        null
                                    }
                                </div>
                            </Card>
                        </div>;
                    } ) }
                </div>
                { selectedProgram.rewardChoiceId && errors.length === 0 && !budgetError &&
                    <div>
                        <div className='behavior-section'>
                            <span className='input-label'><LocaleString code='recognition.program.behaviorHeader' /></span>
                            <span className='input-label-extra'>{ selectedProgram.isBehaviorRequired
                                ? getString( 'recognition.header.required' )
                                : getString( 'recognition.header.optional' ) }</span>
                            <div className='behavior-select-container'>
                                <Select
                                    allowClear={ !selectedProgram.isBehaviorRequired }
                                    data-testid='behaviorSelect'
                                    aria-label={ getString( 'recognition.program.behaviorSelectPlaceholder' ) }
                                    bordered={ false }
                                    size="large"
                                    onChange={ ( behaviorId ) => dispatch( setSelectedBehavior( behaviorId ) ) }
                                    className="behavior-select"
                                    value={ selectedBehaviorId ? selectedBehaviorId : undefined }
                                    placeholder={ getString( 'recognition.program.behaviorSelectPlaceholder' ) }>
                                    {
                                        behaviors.map( behavior => {
                                            return (
                                                <Option key={ behavior.id } data-testid='select-option' title={ behavior.name } >

                                                    <Avatar alt='' className="behavior-image" src={ behavior.imageUrl }></Avatar>
                                                    { behavior.name }
                                                </Option>
                                            );
                                        } )
                                    }
                                </Select>
                            </div>
                        </div>
                        { selectedProgram && selectedProgram.hasPoints &&
                            <Awards data-testid='awards-component' company={ company } integrationType={ integrationType } />
                        }
                    </div>
                }
            </div>
        </CSSTransition >

    );
}
